import { useState, useEffect, useRef } from 'react';
import { forumsAPI } from '../api/client';
import type {
  ForumDetail, ForumType, MeetingDay, MeetingWeek, GeocodeSuggestion,
} from '../types';
import { X, Search, MapPin, Loader2, Shield, ImagePlus } from 'lucide-react';

export interface FacilitatorOption {
  id: number;
  full_name: string;
  role: string;
}

interface ForumFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  forum: ForumDetail | null;
  facilitatorCandidates: FacilitatorOption[];
  /** Facilitator self-serve create: lock the facilitator list to this user
      (picker hidden — non-admins can't browse other facilitators anyway). */
  lockedFacilitator?: FacilitatorOption | null;
  onClose: () => void;
  onSaved: (message: string, forumId: number) => void;
}

const MEETING_DAYS: { value: MeetingDay; label: string }[] = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

const MEETING_WEEKS: { value: MeetingWeek; label: string }[] = [
  { value: 'first', label: '1st' },
  { value: 'second', label: '2nd' },
  { value: 'third', label: '3rd' },
  { value: 'fourth', label: '4th' },
  { value: 'last', label: 'Last' },
];

// Common US timezones first; the browser's zone is added if missing.
const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

function timezoneOptions(current: string): string[] {
  const zones = [...TIMEZONES];
  const browser = Intl.DateTimeFormat().resolvedOptions().timeZone;
  for (const z of [browser, current]) {
    if (z && !zones.includes(z)) zones.push(z);
  }
  return zones;
}

// TimeField <-> <input type="time"> ("07:00:00" <-> "07:00").
const toTimeInput = (v: string | null): string => (v ? v.slice(0, 5) : '');
const fromTimeInput = (v: string): string | null => (v ? `${v}:00` : null);

export default function ForumFormModal({
  isOpen, mode, forum, facilitatorCandidates, lockedFacilitator, onClose, onSaved,
}: ForumFormModalProps) {
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [stateField, setStateField] = useState('');
  const [forumType, setForumType] = useState<ForumType>('mixed');
  const [monthlyPrice, setMonthlyPrice] = useState('400.00');
  const [advisorPrice, setAdvisorPrice] = useState('115.00');
  const [locationName, setLocationName] = useState('');
  const [meetingAddress, setMeetingAddress] = useState('');
  const [latitude, setLatitude] = useState<string | null>(null);
  const [longitude, setLongitude] = useState<string | null>(null);
  const [meetingDay, setMeetingDay] = useState<MeetingDay | ''>('');
  const [meetingWeek, setMeetingWeek] = useState<MeetingWeek | ''>('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [meetingTimezone, setMeetingTimezone] = useState('America/New_York');
  const [facilitatorIds, setFacilitatorIds] = useState<number[]>([]);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  const [facilitatorSearch, setFacilitatorSearch] = useState('');
  const [suggestions, setSuggestions] = useState<GeocodeSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Skip the autocomplete fetch on the render that programmatically sets the
  // address (initial load or picking a suggestion).
  const skipNextSearch = useRef(true);

  useEffect(() => {
    if (!isOpen) return;
    skipNextSearch.current = true;
    if (mode === 'edit' && forum) {
      setName(forum.name);
      setCity(forum.city);
      setStateField(forum.state);
      setForumType(forum.forum_type);
      setMonthlyPrice(forum.monthly_price);
      setAdvisorPrice(forum.advisor_monthly_price);
      setLocationName(forum.location_name || '');
      setMeetingAddress(forum.meeting_address || '');
      setLatitude(forum.latitude);
      setLongitude(forum.longitude);
      setMeetingDay(forum.meeting_day || '');
      setMeetingWeek(forum.meeting_week || '');
      setStartTime(toTimeInput(forum.meeting_start_time));
      setEndTime(toTimeInput(forum.meeting_end_time));
      setMeetingTimezone(forum.meeting_timezone || 'America/New_York');
      setFacilitatorIds(forum.facilitators.map(f => f.id));
      setPhotoUrl(forum.location_photo);
    } else {
      setName('');
      setCity('');
      setStateField('');
      setForumType('mixed');
      setMonthlyPrice('400.00');
      setAdvisorPrice('115.00');
      setLocationName('');
      setMeetingAddress('');
      setLatitude(null);
      setLongitude(null);
      setMeetingDay('');
      setMeetingWeek('');
      setStartTime('');
      setEndTime('');
      setMeetingTimezone(
        Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
      );
      setFacilitatorIds(lockedFacilitator ? [lockedFacilitator.id] : []);
      setPhotoUrl(null);
    }
    setFacilitatorSearch('');
    setSuggestions([]);
    setShowSuggestions(false);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, forum?.id]);

  // Debounced Nominatim autocomplete (>=3 chars, ~400ms) via the backend proxy.
  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return;
    }
    const q = meetingAddress.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setGeoLoading(true);
    const handle = setTimeout(async () => {
      try {
        const results = await forumsAPI.geocodeSearch(q);
        setSuggestions(results);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      } finally {
        setGeoLoading(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [meetingAddress]);

  const applySuggestion = (s: GeocodeSuggestion) => {
    skipNextSearch.current = true;
    setMeetingAddress(s.address_line || s.display_name);
    if (s.city) setCity(s.city);
    if (s.state) setStateField(s.state);
    setLatitude(s.latitude);
    setLongitude(s.longitude);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const toggleFacilitator = (id: number) => {
    setFacilitatorIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !forum) return;
    setUploadingPhoto(true);
    setError(null);
    try {
      const updated = await forumsAPI.uploadPhoto(forum.id, file);
      setPhotoUrl(updated.location_photo);
    } catch {
      setError('Failed to upload photo. Use a JPEG, PNG, or WebP under 8MB.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (facilitatorIds.length === 0) {
      setError('Assign at least one facilitator to this Forum.');
      return;
    }
    setSubmitting(true);
    setError(null);
    // The backend stores 6 decimal places (~0.1m); geocoder results can carry
    // more precision, which would fail server-side validation.
    const round6 = (v: string | null) => {
      if (!v) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n.toFixed(6) : null;
    };
    const payload = {
      name,
      city,
      state: stateField,
      meeting_address: meetingAddress,
      forum_type: forumType,
      location_name: locationName,
      latitude: round6(latitude),
      longitude: round6(longitude),
      meeting_day: meetingDay,
      meeting_week: meetingWeek,
      meeting_start_time: fromTimeInput(startTime),
      meeting_end_time: fromTimeInput(endTime),
      meeting_timezone: meetingTimezone,
      monthly_price: monthlyPrice,
      advisor_monthly_price: advisorPrice,
      facilitator_ids: facilitatorIds,
    };
    try {
      let saved;
      if (mode === 'edit' && forum) {
        saved = await forumsAPI.update(forum.id, payload);
        onSaved('Forum updated successfully', saved.id);
      } else {
        saved = await forumsAPI.create(payload);
        onSaved('Forum created successfully', saved.id);
      }
      onClose();
    } catch (err: unknown) {
      const resp = (err as { response?: { data?: Record<string, unknown> } })?.response;
      const data = resp?.data;
      const facErr = data?.facilitator_ids;
      if (facErr) {
        setError(Array.isArray(facErr) ? String(facErr[0]) : String(facErr));
      } else if (data && typeof data === 'object' && Object.keys(data).length > 0) {
        // Surface the first server-side field error so the user can actually
        // fix it (a bare "check the fields" hides which field is wrong).
        const [field, messages] = Object.entries(data)[0];
        const message = Array.isArray(messages) ? String(messages[0]) : String(messages);
        const label = field === 'non_field_errors' ? '' : `${field.replace(/_/g, ' ')}: `;
        setError(`Couldn't ${mode === 'edit' ? 'update' : 'create'} the Forum — ${label}${message}`);
      } else {
        setError(`Failed to ${mode === 'edit' ? 'update' : 'create'} Forum. Please check the fields and try again.`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const filteredCandidates = facilitatorCandidates.filter(c =>
    c.full_name.toLowerCase().includes(facilitatorSearch.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold">
            {mode === 'edit' ? `Edit ${forum?.name ?? 'Forum'}` : 'Create New Forum'}
          </h3>
          <button onClick={onClose} aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-6 overflow-y-auto">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Forum Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g., Braselton Men's Forum"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
            />
          </div>

          {/* Structured location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Venue Name</label>
            <input
              type="text"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              placeholder="e.g., Virtue Village"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
            />
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Venue Address</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={meetingAddress}
                onChange={(e) => setMeetingAddress(e.target.value)}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Start typing an address…"
                autoComplete="off"
                className="w-full pl-10 pr-9 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
              />
              {geoLoading && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
              )}
            </div>
            {showSuggestions && suggestions.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                {suggestions.map((s, i) => (
                  <li key={`${s.display_name}-${i}`}>
                    <button
                      type="button"
                      onClick={() => applySuggestion(s)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-start gap-2"
                    >
                      <MapPin className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                      <span className="text-gray-700">{s.display_name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Powered by OpenStreetMap. Selecting a result fills city, state, and coordinates.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
                placeholder="Braselton"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <input
                type="text"
                value={stateField}
                onChange={(e) => setStateField(e.target.value)}
                required
                placeholder="GA"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
              />
            </div>
          </div>

          {/* Meeting schedule */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Week of Month</label>
              <select
                value={meetingWeek}
                onChange={(e) => setMeetingWeek(e.target.value as MeetingWeek | '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
              >
                <option value="">—</option>
                {MEETING_WEEKS.map(w => (
                  <option key={w.value} value={w.value}>{w.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Day</label>
              <select
                value={meetingDay}
                onChange={(e) => setMeetingDay(e.target.value as MeetingDay | '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
              >
                <option value="">—</option>
                {MEETING_DAYS.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
              <select
                value={meetingTimezone}
                onChange={(e) => setMeetingTimezone(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
              >
                {timezoneOptions(meetingTimezone).map(z => (
                  <option key={z} value={z}>{z.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-xs text-gray-500 -mt-2">
            Saving updates the Forum calendar too — upcoming meetings, add-to-calendar
            files, and connected calendars follow this schedule.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Forum Type</label>
            <select
              value={forumType}
              onChange={(e) => setForumType(e.target.value as ForumType)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
            >
              <option value="mens">Men's Forum</option>
              <option value="womens">Women's Forum</option>
              <option value="mixed">Combined Forum</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Member Price (USD)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={monthlyPrice}
                  onChange={(e) => setMonthlyPrice(e.target.value)}
                  className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Advisor Price (USD)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={advisorPrice}
                  onChange={(e) => setAdvisorPrice(e.target.value)}
                  className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
                />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 -mt-2">
            Members and advisors share the same Forum experience; advisors are billed the advisor rate.
          </p>

          {/* Facilitator picker (required); locked to the creator for
              facilitator self-serve creation. */}
          {lockedFacilitator ? (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Shield className="w-4 h-4 text-gold" />
                Facilitator
              </label>
              <p className="text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2 bg-off-white">
                {lockedFacilitator.full_name} (you)
              </p>
              <p className="text-xs text-gray-500 mt-1">
                You'll facilitate this Forum. An admin can add co-facilitators later.
              </p>
            </div>
          ) : (
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              <Shield className="w-4 h-4 text-gold" />
              Facilitators <span className="text-red-500">*</span>
            </label>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={facilitatorSearch}
                onChange={(e) => setFacilitatorSearch(e.target.value)}
                placeholder="Search facilitators…"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
              />
            </div>
            <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-gray-100">
              {filteredCandidates.length === 0 && (
                <div className="p-3 text-sm text-gray-500">No matching facilitators or admins.</div>
              )}
              {filteredCandidates.map(c => (
                <label key={c.id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={facilitatorIds.includes(c.id)}
                    onChange={() => toggleFacilitator(c.id)}
                    className="w-4 h-4 text-gold rounded border-gray-300 focus:ring-gold"
                  />
                  <span className="text-sm text-gray-900">{c.full_name}</span>
                  <span className="text-xs text-gray-500 capitalize ml-auto">{c.role}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {facilitatorIds.length} selected — at least one is required.
            </p>
          </div>
          )}

          {/* Location photo (edit only — needs a saved forum to attach to) */}
          {mode === 'edit' && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <ImagePlus className="w-4 h-4 text-gold" />
                Location Photo
              </label>
              <div className="flex items-center gap-4">
                {photoUrl ? (
                  <img src={photoUrl} alt="Meeting location" className="w-20 h-20 object-cover rounded-lg border border-gray-200" />
                ) : (
                  <div className="w-20 h-20 rounded-lg border border-dashed border-gray-300 flex items-center justify-center text-gray-300">
                    <ImagePlus className="w-6 h-6" />
                  </div>
                )}
                <label className="cursor-pointer text-sm text-gold hover:text-navy flex items-center gap-2">
                  {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                  {uploadingPhoto ? 'Uploading…' : photoUrl ? 'Replace photo' : 'Upload photo'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoChange}
                    disabled={uploadingPhoto}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark transition disabled:opacity-60 flex items-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === 'edit' ? 'Save Changes' : 'Create Forum'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
